import { makeAutoObservable } from "mobx";
import { debounce, identity, uniqueId } from "lodash";
import { DocumentLanguage, LocalizedText } from "@/common/common";
import {
  documentLanguageFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  articleSlot,
  ArticleType,
  articleTypeInfo,
  ArticleTypeInfo,
  ArticleValue,
  CeoAppointment,
  isArticleTypeAlwaysAvailable,
  Municipality,
  SELECTABLE_ARTICLE_TYPES,
  SHARE_CLASS_LETTERS,
  ShareClassLetter,
  SIGNATURE_RIGHTS,
  SignatureRight,
  signatureRightInfo,
} from "@/common/articleTypes";
import {
  DbotEvent,
  DbotEventDispatcher,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
} from "@/dbot/common/commonDomain";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { formatNumber } from "@/utils/numbers";
import { move } from "@/utils/arrays";

export type IncorporationArticle = {
  type: ArticleType;
  value?: ArticleValue;
  titles: LocalizedText[];
  texts: LocalizedText[];
};

export type IncorporationArticles = {
  languages: Language[];
  articles: IncorporationArticle[];
};

export type ArticleSummary = {
  paragraph: number;
  title: string;
  text: string;
};

export type CustomArticleSummary = ArticleSummary & { id: string };

type ArticleItem = {
  id: string;
  article: IncorporationArticle;
};

export type AutoArticleValues = {
  companyName: string;
  statutoryPurpose: string;
  shareCapital: number;
  numberOfShares: number;
  shareNominalValue: number;
};

export const DEFAULT_ARTICLES: IncorporationArticle[] = [
  {
    type: ArticleType.RIGHT_OF_FIRST_REFUSAL,
    titles: [
      { language: Language.NO, text: "Aksjenes omsettelighet" },
      { language: Language.EN, text: "Transferability of Shares" },
    ],
    texts: [
      {
        language: Language.NO,
        text: "Aksjonærer har ikke forkjøpsrett til aksjer som skifter eier.",
      },
      {
        language: Language.EN,
        text: "Shareholders do not have pre-emptive rights when shares change owner.",
      },
    ],
  },
  {
    type: ArticleType.ACQUISITION_CONSENT,
    titles: [
      { language: Language.NO, text: "Samtykkekrav ved eierskifte" },
      { language: Language.EN, text: "Consent Requirements for Change of Ownership" },
    ],
    texts: [
      {
        language: Language.NO,
        text: "Erverv av aksjer i selskapet, samt pantsettelse av aksjer, krever ikke samtykke fra selskapet.",
      },
      {
        language: Language.EN,
        text: "Acquisition of shares in the company, and pledging of shares, does not require consent from the company.",
      },
    ],
  },
];

const ARTICLE_DOCUMENT_LANGUAGES: DocumentLanguage[] = ["no", "no/en"];

export function autoArticles(values: AutoArticleValues): ArticleSummary[] {
  const nok = (amount: number) =>
    `kr ${formatNumber(Language.NO, amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return [
    {
      paragraph: 1,
      title: "Foretaksnavn",
      text: `Selskapets foretaksnavn er ${values.companyName}.`,
    },
    { paragraph: 2, title: "Formål", text: values.statutoryPurpose },
    {
      paragraph: 3,
      title: "Selskapets aksjekapital",
      text: `Selskapets aksjekapital er ${nok(values.shareCapital)} fordelt på ${formatNumber(Language.NO, values.numberOfShares)} aksjer, hver pålydende ${nok(values.shareNominalValue)}.`,
    },
  ];
}

const AUTO_ARTICLE_COUNT = 3;

export function textIn(texts: LocalizedText[] | undefined, language: Language): string {
  return texts?.find((t) => t.language === language)?.text ?? "";
}

function articleItem(article: IncorporationArticle): ArticleItem {
  return { id: uniqueId("article-"), article };
}

function articleSummary(
  article: IncorporationArticle,
  index: number,
  language: Language,
): ArticleSummary {
  return {
    paragraph: AUTO_ARTICLE_COUNT + index + 1,
    title: textIn(article.titles, language),
    text: textIn(article.texts, language),
  };
}

export class IncorporationArticlesModel {
  isCustomModel: RadioFieldModel<boolean>;
  documentLanguageModel: RadioFieldModel<DocumentLanguage>;
  items: ArticleItem[];
  dialog?: IncorporationArticleDialogModel;
  error?: TMessage;

  constructor(
    private readonly dispatch: DbotEventDispatcher,
    saved?: IncorporationArticles,
  ) {
    this.isCustomModel = new RadioFieldModel<boolean>({
      value: saved !== undefined,
      options: [false, true],
      idFn: identity,
      labelFn: (isCustom) => (isCustom ? { key: "custom" } : { key: "common.auto" }),
    });
    this.documentLanguageModel = documentLanguageFieldModel({
      value: mapLocalesToDocumentLanguage(saved?.languages),
      options: ARTICLE_DOCUMENT_LANGUAGES,
      onChange: () => this.revalidate(),
    });
    this.items = (saved?.articles ?? DEFAULT_ARTICLES).map(articleItem);
    makeAutoObservable<this, "dispatch">(this, { dispatch: false }, { autoBind: true });
  }

  handleEvent(event: DbotEvent) {
    if (event.type === "dbot/search-municipalities-result") {
      this.dialog?.applyMunicipalities(event.source, event.result);
    }
  }

  get isCustom(): boolean {
    return this.isCustomModel.value;
  }

  get languages(): Language[] {
    return mapDocumentLanguageToLocales(this.documentLanguageModel.value);
  }

  get articles(): IncorporationArticle[] {
    return this.items.map((item) => item.article);
  }

  get customArticles(): IncorporationArticles | undefined {
    return this.isCustom ? { languages: this.languages, articles: this.articles } : undefined;
  }

  get summaries(): ArticleSummary[] {
    return (this.isCustom ? this.articles : DEFAULT_ARTICLES).map((article, index) =>
      articleSummary(article, index, this.languages[0]),
    );
  }

  get customSummaries(): CustomArticleSummary[] {
    return this.items.map((item, index) => ({
      id: item.id,
      ...articleSummary(item.article, index, this.languages[0]),
    }));
  }

  isTypeTaken(type: ArticleType): boolean {
    return (
      !isArticleTypeAlwaysAvailable(type) && this.articles.some((article) => article.type === type)
    );
  }

  get isValid(): boolean {
    return (
      !this.isCustom ||
      this.articles.every((article) =>
        this.languages.every((language) => textIn(article.texts, language).trim() !== ""),
      )
    );
  }

  validate(): boolean {
    this.error = this.isValid
      ? undefined
      : { key: "decisions.incorporation.articles-missing-text" };
    return this.isValid;
  }

  private revalidate() {
    if (this.error) {
      this.validate();
    }
  }

  openAddDialog(type: ArticleType) {
    const lastParagraph = AUTO_ARTICLE_COUNT + this.articles.length + 1;
    this.dialog = new IncorporationArticleDialogModel({
      dispatch: this.dispatch,
      languages: this.languages,
      isRichText: false,
      paragraphs: this.paragraphs(lastParagraph),
      paragraph: lastParagraph,
      type,
      takenSlots: this.slotsExcept(),
      onSubmit: (article, paragraph) => this.place(articleItem(article), paragraph, this.items),
      onClose: this.closeDialog,
    });
  }

  openEditDialog(index: number) {
    const others = this.items.filter((_, i) => i !== index);
    const { id, article } = this.items[index];
    this.dialog = new IncorporationArticleDialogModel({
      dispatch: this.dispatch,
      languages: this.languages,
      isRichText: false,
      paragraphs: this.paragraphs(AUTO_ARTICLE_COUNT + this.articles.length),
      paragraph: AUTO_ARTICLE_COUNT + index + 1,
      type: article.type,
      article,
      takenSlots: this.slotsExcept(index),
      onSubmit: (edited, paragraph) => this.place({ id, article: edited }, paragraph, others),
      onClose: this.closeDialog,
    });
  }

  removeArticle(index: number) {
    this.items = this.items.filter((_, i) => i !== index);
    this.revalidate();
  }

  moveArticle(id: string, toId: string) {
    const from = this.items.find((item) => item.id === id);
    if (from) {
      this.items = move(
        this.items,
        from,
        this.items.findIndex((item) => item.id === toId),
      );
    }
  }

  closeDialog() {
    this.dialog = undefined;
  }

  private paragraphs(last: number): number[] {
    return Array.from({ length: last - AUTO_ARTICLE_COUNT }, (_, i) => AUTO_ARTICLE_COUNT + i + 1);
  }

  private slotsExcept(index?: number): Set<string> {
    return new Set(
      this.articles
        .filter((_, i) => i !== index)
        .map((article) => articleSlot(article.type, article.value))
        .filter((slot): slot is string => slot !== undefined),
    );
  }

  private place(item: ArticleItem, paragraph: number, others: ArticleItem[]) {
    const index = paragraph - AUTO_ARTICLE_COUNT - 1;
    this.items = [...others.slice(0, index), item, ...others.slice(index)];
    this.closeDialog();
    this.revalidate();
  }
}

export type IncorporationArticleDialogParams = {
  dispatch: DbotEventDispatcher;
  languages: Language[];
  isRichText: boolean;
  paragraphs: number[];
  paragraph: number;
  type: ArticleType;
  article?: IncorporationArticle;
  takenSlots: Set<string>;
  onSubmit: (article: IncorporationArticle, paragraph: number) => void;
  onClose: () => void;
};

export class IncorporationArticleDialogModel {
  readonly isEdit: boolean;
  readonly languages: Language[];
  readonly isRichText: boolean;
  readonly onClose: () => void;
  paragraphModel: RadioFieldModel<number>;
  typeModel: RadioFieldModel<ArticleType>;
  titles: LocalizedStringFieldModel[];
  texts: LocalizedStringFieldModel[];
  numberModel: NumberFieldModel;
  yesNoModel: RadioFieldModel<boolean>;
  ceoAppointmentModel: RadioFieldModel<CeoAppointment>;
  shareClassModel: RadioFieldModel<ShareClassLetter>;
  signatureRightModel: RadioFieldModel<SignatureRight>;
  municipalitySearchModel: StringFieldModel;
  suggestedMunicipalities: Municipality[] = [];
  isSearchingMunicipalities = false;
  searchMunicipalities: (input: string) => void;
  municipality?: Municipality;
  municipalityError?: TMessage;
  typeError?: TMessage;
  private readonly takenSlots: Set<string>;
  private readonly dispatch: DbotEventDispatcher;
  private readonly onSubmit: IncorporationArticleDialogParams["onSubmit"];

  constructor(params: IncorporationArticleDialogParams) {
    const { article } = params;
    const value = article?.value;
    this.isEdit = article !== undefined;
    this.dispatch = params.dispatch;
    this.languages = params.languages;
    this.isRichText = params.isRichText;
    this.takenSlots = params.takenSlots;
    this.onSubmit = params.onSubmit;
    this.onClose = params.onClose;
    this.paragraphModel = new RadioFieldModel<number>({
      value: params.paragraph,
      options: params.paragraphs,
      idFn: identity,
      labelFn: (paragraph) => ({ translated: `§ ${paragraph}` }),
    });
    this.typeModel = new RadioFieldModel<ArticleType>({
      value: params.type,
      options: SELECTABLE_ARTICLE_TYPES.map((info) => info.type),
      idFn: identity,
      labelFn: (type) => articleTypeInfo(type).name,
      onChange: (type) => this.applyType(type),
    });
    const defaultTitles = articleTypeInfo(params.type).defaultTitles;
    this.titles = params.languages.map(
      (language) =>
        new LocalizedStringFieldModel(
          new StringFieldModel({
            value: article ? textIn(article.titles, language) : (defaultTitles[language] ?? ""),
          }),
          language,
        ),
    );
    this.texts = params.languages.map(
      (language) =>
        new LocalizedStringFieldModel(
          new StringFieldModel({
            value: textIn(article?.texts, language),
            required: true,
            validator: (text) =>
              text.length > this.info.maxLength ? { key: "text-too-long" } : undefined,
          }),
          language,
        ),
    );
    this.numberModel = new NumberFieldModel({
      value: value?.kind === "number" ? value.number : undefined,
      required: true,
      isInteger: true,
      isPositive: true,
    });
    this.yesNoModel = new RadioFieldModel<boolean>({
      value: value?.kind === "yesNo" ? value.isYes : true,
      options: [true, false],
      idFn: identity,
      labelFn: (isYes) => (isYes ? { key: "yes" } : { key: "no" }),
    });
    this.ceoAppointmentModel = new RadioFieldModel<CeoAppointment>({
      value: value?.kind === "ceoAppointment" ? value.appointedBy : CeoAppointment.GENERAL_MEETING,
      options: Object.values(CeoAppointment),
      idFn: identity,
      labelFn: (appointedBy) =>
        appointedBy === CeoAppointment.GENERAL_MEETING
          ? { key: "conduct-general-meeting" }
          : { key: "article-type.corporate-assembly" },
    });
    this.shareClassModel = new RadioFieldModel<ShareClassLetter>({
      value: value?.kind === "shareClass" ? value.shareClass : "A",
      options: [...SHARE_CLASS_LETTERS],
      idFn: identity,
      labelFn: (shareClass) => ({ translated: shareClass }),
      onChange: () => {
        this.typeError = undefined;
      },
    });
    this.signatureRightModel = new RadioFieldModel<SignatureRight>({
      value: value?.kind === "signatureRight" ? value.signatureRight : SignatureRight.CHAIR_ALONE,
      options: SIGNATURE_RIGHTS.map((info) => info.signatureRight),
      idFn: identity,
      labelFn: (right) => signatureRightInfo(right).label,
      onChange: () => this.applySignatureRight(),
    });
    if (this.isTextFixed) {
      this.applySignatureRight();
    }
    this.municipality = value?.kind === "municipality" ? value.municipality : undefined;
    this.municipalitySearchModel = new StringFieldModel({ value: this.municipality?.name });
    this.searchMunicipalities = debounce(
      (input: string) =>
        this.dispatch([{ type: "dbot/search-municipalities", input, source: this }]),
      250,
    );
    makeAutoObservable<this, "dispatch">(
      this,
      { dispatch: false, searchMunicipalities: false },
      { autoBind: true },
    );
  }

  get info(): ArticleTypeInfo {
    return articleTypeInfo(this.typeModel.value);
  }

  get isTextFixed(): boolean {
    return this.info.valueKind === "signatureRight";
  }

  get value(): ArticleValue | undefined {
    switch (this.info.valueKind) {
      case "number": {
        const number = this.numberModel.toNumber();
        return number === undefined ? undefined : { kind: "number", number };
      }
      case "yesNo":
        return { kind: "yesNo", isYes: this.yesNoModel.value };
      case "municipality":
        return this.municipality && { kind: "municipality", municipality: this.municipality };
      case "ceoAppointment":
        return { kind: "ceoAppointment", appointedBy: this.ceoAppointmentModel.value };
      case "shareClass":
        return { kind: "shareClass", shareClass: this.shareClassModel.value };
      case "signatureRight":
        return { kind: "signatureRight", signatureRight: this.signatureRightModel.value };
      case "none":
        return undefined;
    }
  }

  get isValid(): boolean {
    return (
      [...this.titles, ...this.texts].every((field) => field.model.isValid) &&
      this.typeError === undefined &&
      this.municipalityError === undefined &&
      (this.info.valueKind !== "number" || this.numberModel.isValid)
    );
  }

  setMunicipalitySearch(input: string) {
    this.municipalitySearchModel.setInputValue(input);
    this.municipality = undefined;
    this.isSearchingMunicipalities = input.trim() !== "";
    if (this.isSearchingMunicipalities) {
      this.searchMunicipalities(input);
    } else {
      this.suggestedMunicipalities = [];
    }
  }

  applyMunicipalities(source: unknown, municipalities: Municipality[]) {
    if (source === this) {
      this.suggestedMunicipalities = municipalities;
      this.isSearchingMunicipalities = false;
    }
  }

  selectMunicipality(municipality: Municipality) {
    this.municipality = municipality;
    this.municipalitySearchModel.setAndCommitInputValue(municipality.name);
    this.municipalityError = undefined;
  }

  clearMunicipality() {
    this.municipality = undefined;
    this.municipalitySearchModel.setAndCommitInputValue("");
  }

  private applyType(type: ArticleType) {
    const previous = this.titles.map((field) => field.model.inputValue.trim());
    this.titles.forEach((field, i) => {
      const isUntouched =
        previous[i] === "" ||
        SELECTABLE_ARTICLE_TYPES.some((info) => info.defaultTitles[field.locale] === previous[i]);
      if (isUntouched) {
        field.model.setAndCommitInputValue(articleTypeInfo(type).defaultTitles[field.locale] ?? "");
      }
    });
    this.typeError = undefined;
    this.municipalityError = undefined;
    if (articleTypeInfo(type).valueKind === "signatureRight") {
      this.applySignatureRight();
    }
  }

  private applySignatureRight() {
    const texts = signatureRightInfo(this.signatureRightModel.value).texts;
    this.texts.forEach((field) => field.model.setAndCommitInputValue(texts[field.locale] ?? ""));
  }

  validate(): boolean {
    [...this.titles, ...this.texts].forEach((field) => {
      field.model.commit();
      field.model.validate();
    });
    if (this.info.valueKind === "number") {
      this.numberModel.validate();
    }
    this.municipalityError =
      this.info.valueKind === "municipality" && this.municipality === undefined
        ? { key: "required" }
        : undefined;
    const slot = articleSlot(this.typeModel.value, this.value);
    this.typeError =
      slot !== undefined && this.takenSlots.has(slot) ? { key: "article.type-taken" } : undefined;
    return this.isValid;
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(
        {
          type: this.typeModel.value,
          value: this.value,
          titles: this.titles.map((field) => ({ language: field.locale, text: field.text })),
          texts: this.texts.map((field) => ({ language: field.locale, text: field.text })),
        },
        this.paragraphModel.value,
      );
    }
  }
}
